/**
 * DemocratCharts — instrument search.
 *
 * Backed by /api/instruments/search, which reads the archive database. That
 * includes contracts which have EXPIRED: their bars still exist locally and
 * nowhere else, since neither Kite nor Arrow will serve a token once its
 * contract is gone. Those come back flagged so the UI can show them as
 * chartable-but-not-tradeable rather than hiding them.
 *
 * Uses the shared axios client so search inherits bearer injection and the
 * 401-refresh-retry that every other call gets.
 */
import { api } from "@/lib/api";
import type { SymbolEntry } from "@/lib/symbols";

export interface Instrument {
  symbol: string;
  exchange: string;
  name: string;
  instrument_type: string;
  expiry: string | null;
  strike: number | null;
  lot_size: number | null;
  expired: boolean;
}

export interface InstrumentSearchResponse {
  query: string;
  source: string;
  count: number;
  results: Instrument[];
}

/** Human label for the dropdown row. */
export function describe(i: Instrument): string {
  const bits: string[] = [];
  if (i.name && i.name !== i.symbol) bits.push(i.name);
  if (i.instrument_type === "CE" || i.instrument_type === "PE") {
    if (i.strike) bits.push(`${i.strike} ${i.instrument_type}`);
  } else if (i.instrument_type === "FUT") {
    bits.push("Future");
  }
  if (i.expiry) bits.push(i.expiry);
  return bits.join(" · ");
}

/** Adapt to the shape the existing components already pass around. */
export function toSymbolEntry(i: Instrument): SymbolEntry {
  const category: SymbolEntry["category"] =
    i.instrument_type === "INDEX"
      ? "Indian Index"
      : i.instrument_type === "EQ"
        ? "Indian Equity"
        : "Derivatives";
  return {
    symbol: i.symbol,
    exchange: i.exchange,
    name: describe(i) || i.symbol,
    category,
  };
}

export async function searchInstruments(
  q: string,
  signal?: AbortSignal
): Promise<Instrument[]> {
  const query = q.trim();
  if (!query) return [];
  const { data } = await api.get<InstrumentSearchResponse>(
    "/instruments/search",
    { params: { q: query, limit: 50 }, signal }
  );
  return data.results ?? [];
}
